// @flow

import { useEffect, useRef } from 'react';
import { useTenant } from '@tectonics/commons/tenant';
import { usePayment } from '../context';
import { queryAssertionOptions } from '../services/payment-api';
import type { PaymentState, PaymentAction } from '../context';
import type { AssertionOptions } from '../types';

const POLLING_INTERVAL_MS = 3000;
const POLLING_TIMEOUT_MS = 120000;

export type QueryResult = {|
  +status: string,
  +assertion_options?: AssertionOptions,
  +redirect_url_after_payment?: string,
  +status_message?: string,
|};

type PollingConfig = {|
  +activeState: PaymentState,
  +onResult: (result: QueryResult, dispatch: (PaymentAction) => void) => boolean,
  +timeoutMessage: string,
|};

export function usePolling(config: PollingConfig) {
  const tenant = useTenant();
  const { data, dispatch } = usePayment();
  const pollingStartRef = useRef<?number>(null);
  const timerRef = useRef<?TimeoutID>(null);

  useEffect(() => {
    if (data.state !== config.activeState) {
      return;
    }

    let cancelled = false;
    pollingStartRef.current = Date.now();

    async function poll() {
      if (cancelled) return;

      const startTime = pollingStartRef.current;
      if (startTime && Date.now() - startTime > POLLING_TIMEOUT_MS) {
        if (!cancelled) {
          dispatch({ type: 'SET_ERROR', errorMessage: config.timeoutMessage });
        }
        return;
      }

      try {
        const result = await queryAssertionOptions(data.paymentHash, tenant.code);

        if (cancelled) return;

        const handled = config.onResult(result, dispatch);
        if (handled) return;
      } catch (_e) {
        // Transient errors (network failures, etc.) are expected during polling;
        // the next polling interval will retry automatically.
        if (cancelled) return;
      }

      if (!cancelled) {
        timerRef.current = setTimeout(poll, POLLING_INTERVAL_MS);
      }
    }

    poll();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pollingStartRef.current = null;
    };
  }, [data.state, data.paymentHash, tenant.code, dispatch, config]);
}
